import { logger } from "c7-logger/index";

import {
  base64Encode,
  encryptData,
  importPublicKey,
  payloadDataToBuffer,
} from "./tools";

export type IEncrypt = {
  value: string;
  key: string;
};

export type IEncryptData = {
  encryptionNonce: string;
  encryptionTimestamp: number;
  encryptedData: string;
};

export const valueEncrypt = async ({
  value,
  key,
}: IEncrypt): Promise<IEncryptData> => {
  // to prevent replay attacks
  const encryptionNonce = window.crypto.randomUUID();
  const encryptionTimestamp = Date.now();
  const rawPan = `${value}:${encryptionNonce}:${encryptionTimestamp}`;

  const dataBuffer = payloadDataToBuffer(rawPan);
  const cryptoKey = await importPublicKey(key);
  const buffer = await encryptData({ cryptoKey, dataBuffer });
  return {
    encryptedData: base64Encode(buffer),
    encryptionNonce,
    encryptionTimestamp,
  };
};

export const isEncryptionFunctioning = async (
  key: IEncrypt["key"]
): Promise<boolean> => {
  if (key === "noop") {
    return true;
  }
  try {
    await valueEncrypt({ value: "test", key });
    return true;
  } catch (e) {
    logger.error("isEncryptionFunctioning error", e);
    return false;
  }
};

// Hash function proxy
const valueHash = async (value: ArrayBuffer): Promise<ArrayBuffer> => {
  return await window.crypto.subtle.digest("SHA-512", value);
};

// Loop hash function X times
const valueHashLoop = async (
  value: string,
  loopCount: number
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);

  let hashStore: ArrayBuffer = data;

  for (let i = 0; i < loopCount; i++) {
    hashStore = await valueHash(hashStore);
  }

  return base64Encode(hashStore);
};

// This hashes with no loops
export const valueHashOnce = async (value: string): Promise<string> => {
  return await valueHashLoop(value, 1);
};
